import React, { PureComponent, Suspense } from 'react';
import PropTypes from 'prop-types';
import { BroadcastChannel } from 'broadcast-channel';
import { connect } from 'react-redux';
import translationInit from '../../utils/i18n';
import * as actions from '../../actions/AppActions';
import Navigation from '../Navigation/Navigation';
import PopupAlert from '../PopupAlert/PopupAlert';
import Workplace from '../Workplace/Workplace';
import Toast from '../Toast/Toast';
import { ThemeContext } from './ThemeContext';
import * as API from '../../utils/API';
import classes from './App.module.scss';

function mapStateToProps(state) {
  return {
    fillGlossary: state.main.fillGlossary,
    fillCommonData: state.main.fillCommonData,
    fillSelected: state.main.fillSelected,
    filterContent: state.main.filterContent,
    fillModules: state.main.fillModules,
    glossary: state.main.glossary,
    selected: state.main.selected,
    contents: state.main.contents,
    commonSettings: state.main.commonSettings,
    fillStartPage: state.startPage.fillStartPage,
    startPage: state.startPage.startPage,
  };
}

const Glossary = React.lazy(() => import('../Glossary/Glossary'));
const Impaired = React.lazy(() => import('../Impaired/Impaired'));
const StartPageA = React.lazy(() => import('../StartPage_A/StartPage'));
const StartPageB = React.lazy(() => import('../StartPage_B/StartPage'));
const StartPageC = React.lazy(() => import('../StartPage_C/StartPage'));
const RefreshBtn = React.lazy(() => import('../RefreshBtn/RefreshBtn'));

class App extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      impairedProps: '',
      navBarVisible: true,
      isMultipleTab: false,
      showStartPage: false,
    };

    this.iFrameRef = React.createRef();

    this.onIframeLoad = this.onIframeLoad.bind(this);
  }

  componentDidMount() {
    // для локальной выгрузки
    if (window.params) {
      this.onInit(window.params);
      return;
    }

    API.getInitData().then((data) => {
      this.onInit(data);
    }).catch((error) => {
      throw error;
    });
  }

  onInit(data) {
    const {
      fillGlossary,
      fillStartPage,
      fillCommonData,
      fillSelected,
      fillModules,
      filterContent,
    } = this.props;
    fillGlossary(data.glossary ? data.glossary : {});
    fillStartPage(data.startPage ? data.startPage : {});
    fillCommonData({
      base: data.base,
      courseId: data.courseId ? data.courseId.toString() : '',
      lang: data.lang,
      title: data.title,
      logo: data.logo,
      background: data.background,
      theme: data.theme,
      order: data.order ? data.order.toString() : 'false',
      completeIcon: data.completeIcon ? data.completeIcon.toString() : 'false',
      comments: data.comments ? data.comments.toString() : 'false',
      favorites: data.favorites ? data.favorites.toString() : 'false',
      screenCapture: data.screenCapture ? data.screenCapture.toString() : 'false',
      instructions: data.instructions ? data.instructions.toString() : 'false',
      saveStats: data.saveStats ? data.saveStats.toString() : 'false',
      hideNavbar: data.hideNavbar ? data.hideNavbar.toString() : 'false',
    });

    window.result.statisticUrlShell = data.statisticUrlShell;
    window.result.statisticUrlModule = data.statisticUrlModule;

    let moduleId = null;
    if (window.location.hash.length > 1) moduleId = window.location.hash.substring(1);
    document.title = data.title.replace(/(<br\s*?\\*?\/*?>|&nbsp;)/gi, ' ').replace(/<(\/?)nobr>/gi, '');
    fillSelected(data.contents, moduleId).then(() => {
      filterContent(data.contents, '').then((filteredContent) => {
        fillModules(filteredContent);
        translationInit(data.lang);
        this.setState({
          isLoading: false,
        });
        //this.iFrameRef.current.onload = this.onIframeLoad();
      });
    });

    if (data.oneTabLimit && data.oneTabLimit.toString() === 'true') this.checkMultipleTabs();

    document.querySelector('html').setAttribute('lang', data.lang);

    if (data.startPage) {
      this.setState({
        showStartPage: true,
      });
    }
  }

  onIframeLoad() {
    // для локальной выгрузки не делаем ничего
    if (window.params) return;
    // Удаляет из всех ссылок в учебниках (ebook) атрибут href иначе они не работают
    const links = this.iFrameRef.current.contentWindow.document.querySelectorAll('.link-redirect');
    let i = 0;
    for (i; i < links.length; i += 1) {
      links[i].removeAttribute('href');
    }
  }

  onChangeImpairedParams(props) {
    this.setState({
      impairedProps: props,
    });

    this.iFrameRef.current.contentWindow.postMessage(props, '*');
  }

  onNavBarVisibleChange(flag) {
    this.setState({
      navBarVisible: flag,
    });
  }

  checkMultipleTabs() {
    // Проверка на наличие дублирующих вкладок
    const channel = new BroadcastChannel('shell-react-tab');
    const msgData = `another-tab-${window.location.pathname}`;
    channel.postMessage(msgData);

    const tabIdent = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    channel.addEventListener('message', (msg) => {
      if (msg === msgData) {
        channel.postMessage(`close-${tabIdent}`);
      } else if (msg.indexOf('close') !== -1 && msg !== `close-${tabIdent}`) {
        channel.close();
        this.setState({
          isMultipleTab: true,
        });
      }
    });
  }

  closeStartPage() {
    this.setState({
      showStartPage: false,
    });
  }

  openStartPage() {
    this.setState({
      showStartPage: true,
    });
  }

  render() {
    const { glossary, startPage, contents, selected, commonSettings } = this.props;
    const {
      impairedProps,
      navBarVisible,
      isLoading,
      isMultipleTab,
      showStartPage,
    } = this.state;

    let theme = null;
    if (impairedProps) theme = JSON.parse(impairedProps).scheme;

    let url = `${window.location.origin}${window.location.pathname}?act=index&lesson=${selected.content}`;
    // для локальной выгрузки
    if (window.params) url = `./content/${selected.content}/index.html?act=index`;

    if (isLoading) return null;

    const hasStartPage = Object.keys(startPage).length > 0;

    let StartPage = null;

    if (hasStartPage) {
      switch (startPage.template_type) {
        case "default":
          StartPage = StartPageA;
          break;
        case "disciplines":
          StartPage = StartPageB;
        break;
        case "nested":
          StartPage = StartPageC;
        break;
        default:
          StartPage = StartPageA; 
        break;
      }
    }
    
    return (
      <>
        <Toast />
        <Suspense fallback={null}><PopupAlert theme={theme} /></Suspense>
        {hasStartPage && (
            <Suspense fallback={null}>
              <StartPage
                showStartPage={showStartPage}
                startPage={startPage}
                closeStartPage={() => this.closeStartPage()}
                contents={contents}
                commonSettings={commonSettings}
              />
            </Suspense>
        )}
        { (hasStartPage && !showStartPage) && (
          <Suspense fallback={null}>
            <RefreshBtn template={startPage.template_type} openStartPage={() => this.openStartPage()}/>
          </Suspense>
        )}
        <ThemeContext.Provider value={{ theme }}>
          <Navigation
            iframe={this.iFrameRef.current}
            navBarVisibleChange={(flag) => this.onNavBarVisibleChange(flag)}
          />
          { Object.keys(glossary).length > 0 && (
            <Suspense fallback={null}>
              <Glossary glossary={glossary} behavior={selected.behavior} />
            </Suspense>
          )}
        </ThemeContext.Provider>
        { !showStartPage && (
          <div className={`${classes.Workplace} ${navBarVisible ? classes.WorkplaceShrink : ''}`}>
            {commonSettings.theme === 'inclusive' && (
              <Suspense fallback={null}>
                <Impaired
                  iframe={this.iFrameRef.current}
                  changeImpairedParams={(props) => this.onChangeImpairedParams(props)}
                />
              </Suspense>
            )}
            <Workplace
              moduleSrc={url}
              impairedProps={impairedProps}
              onIframeLoad={this.onIframeLoad}
              ref={this.iFrameRef}
              isMultipleTab={isMultipleTab}
            />
          </div>
        )}
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  actions,
)(App);

App.propTypes = {
  fillGlossary: PropTypes.func.isRequired,
  fillStartPage: PropTypes.func.isRequired,
  fillCommonData: PropTypes.func.isRequired,
  fillSelected: PropTypes.func.isRequired,
  fillModules: PropTypes.func.isRequired,
  filterContent: PropTypes.func.isRequired,
  commonSettings: PropTypes.shape({
    theme: PropTypes.string.isRequired,
  }).isRequired,
  glossary: PropTypes.objectOf(PropTypes.shape({
    image: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
  startPage: PropTypes.shape({
    template_type: PropTypes.string,
    template_theme: PropTypes.string,
    fgosTitle: PropTypes.string,
    fgosText: PropTypes.string,
    mdkTitle: PropTypes.string,
    mdkText: PropTypes.string,
    mainImg: PropTypes.string,
    background: PropTypes.string,
    theme: PropTypes.string,
    info: PropTypes.string,
    tags: PropTypes.string,
    modules: PropTypes.arrayOf(PropTypes.any),
  }),
  selected: PropTypes.objectOf(PropTypes.any).isRequired,
};
